import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import { FromBaseInputMissing, FromBaseStageMissing, fromBaseRecord, fromBaseUuid } from './from-base-context';
import type { ItemFieldRow } from './item-field-reader';
import { readItemField } from './item-field-reader';
import type { StatusReferenceDependencies } from './status-reference-resolution';
import { readInventoryField } from './inventory-raw-readers';
import { compileInventoryTreeFromBase } from './from-base-inventory';
import type { FromBaseInventoryMetadata } from './from-base-inventory';
import { mergeInventoryMetadata } from './inventory-shared-catalog';
import { compileCreatureDisplayName } from './creature-display-name';

const kinds = [ ['items', 'item_id', 'item'], ['player_characters', 'item_id', 'player_character'],
    ['encounter_creatures', 'encounter_creature_id', 'encounter_creature'], ['inventory', 'inventory_id', 'inventory'],
    ['encounters', 'encounter_id', 'encounter'], ['locations', 'location_id', 'location'] ] as const;

/** Shared PC/NPC status-origin reader. Absence is known only after all source tables were read. */
export function fromBaseStatusReferences(base: FromBaseRow, dependencies: {
    rawDefinition(id: string): FromBaseRow | null;
    definition(id: string): ItemFieldRow | null;
    fail(detail: string): never;
    inventory?(id: string): ItemFieldRow | null;
}): StatusReferenceDependencies {
    const source = readItemField(base, 'status_origin_references');
    const stored = (key: string): unknown => fromBaseRecord(source) ? readItemField({ [key]: [source[key] ?? null] }, key) : null;
    const coveredIds = stored('covered_ids');
    const covered = (id: string): boolean => Array.isArray(coveredIds) && coveredIds.some(value => typeof value === 'string' && value.toUpperCase() === id.toUpperCase());
    const rows = (table: string): readonly FromBaseRow[] => {
        const value = stored(table);
        const key = kinds.find(kind => kind[0] === table)?.[1];
        if (!Array.isArray(value) || !key || !value.every(row => fromBaseRecord(row) && fromBaseUuid(readItemField(row, key))))
            return dependencies.fail(`status-origin-table:${table}`);
        return value;
    };
    const find = (table: string, key: string, id: string): FromBaseRow | undefined => rows(table).find(row => {
        const value = readItemField(row, key);
        return typeof value === 'string' && value.toUpperCase() === id.toUpperCase();
    });
    const found = (table: string, key: string, id: string): boolean => find(table, key, id) !== undefined;
    const type = (id: string): string => {
        if (covered(id)) return kinds.find(([table, key]) => found(table, key, id))?.[2] ?? 'not_found';
        if (Array.isArray(base.inventory) && base.inventory.some(row => fromBaseRecord(row)
            && String(readInventoryField(row, 'inventory_id')).toUpperCase() === id.toUpperCase())) return 'inventory';
        return dependencies.definition(id) ? 'item' : dependencies.fail(`origin-type:${id}`);
    };
    return {
        itemRow: id => covered(id) && !found('items', 'item_id', id) ? null : dependencies.rawDefinition(id)?.items ?? null,
        count: (table, column, id) => covered(id) ? Number(found(table, column, id))
            : table === 'items' ? Number(dependencies.rawDefinition(id) !== null) : dependencies.fail(`inventory-origin:${id}`),
        fullItem: dependencies.definition,
        determineType: type,
        dynamicItem: id => {
            if (covered(id)) {
                const kind = type(id);
                if (kind === 'not_found') return [];
                if (kind === 'player_character' || kind === 'encounter_creature') {
                    // Native creature sources serve saved identity, not a recursive creature compile.
                    // They carry no ability payload to merge into the applied status.
                    const pc = find('player_characters', 'item_id', id) ?? null;
                    const ec = find('encounter_creatures', 'encounter_creature_id', id) ?? null;
                    const text = (row: FromBaseRow | null, key: string): string | null => {
                        const value = readItemField(row, key);
                        return typeof value === 'string' ? value : null;
                    };
                    const templateId = text(ec, 'item_id');
                    const template = !pc && templateId && fromBaseRecord(base.catalog) ? base.catalog[templateId.toUpperCase()] : null;
                    return { items: { item_id: id, item_type: pc ? 'player_character' : text(ec, 'item_type') ?? 'creature',
                        item_name: compileCreatureDisplayName({ playerName: text(pc, 'player_name'),
                            encounterCreatureName: text(ec, 'encounter_creature_name'),
                            itemName: text(fromBaseRecord(template) ? template : null, 'item_name') }) } };
                }
            }
            if (type(id) === 'inventory' && dependencies.inventory) {
                const row = dependencies.inventory(id);
                if (row) return row;
            }
            return dependencies.fail(`dynamic-status-source:${id}`);
        },
    };
}

/** Ids the saved SELECT proves are status origins without an item row (an inventory instance, a creature,
 * a deleted source). Catalog interning skips them as the native raw item lookup does; unproven ids stay strict. */
export function fromBaseStatusOriginNonItem(base: FromBaseRow): (id: string) => boolean {
    const source = readItemField(base, 'status_origin_references');
    const stored = (key: string): unknown => fromBaseRecord(source) ? readItemField({ [key]: [source[key] ?? null] }, key) : null;
    const ids = (value: unknown, read: (row: unknown) => unknown): string[] => Array.isArray(value)
        ? value.flatMap(row => { const id = read(row); return fromBaseUuid(id) ? [id.toUpperCase()] : []; }) : [];
    const items = new Set(ids(stored('items'), row => fromBaseRecord(row) ? readItemField(row, 'item_id') : null));
    const inventoryId = (row: unknown) => fromBaseRecord(row) ? readInventoryField(row, 'inventory_id') : null;
    const sources = Array.isArray(base.status_origin_inventory_sources) ? base.status_origin_inventory_sources as unknown[] : [];
    const proven = new Set([...ids(stored('covered_ids'), row => row).filter(id => !items.has(id)), ...ids(base.inventory, inventoryId),
        ...sources.flatMap(snapshot => ids(fromBaseRecord(snapshot) ? snapshot.inventory : null, inventoryId))]);
    return id => proven.has(id.toUpperCase());
}

export type StatusOriginInventoryBase = Pick<FromBasePlayerInput, 'catalog' | 'status_origin_inventory_sources' | 'enchantment_templates'
    | 'enchantment_template_enchantments' | 'missing_definition_ids' | 'definition_videos' | 'pl_signature' | 'inventory_metadata'>;

/** Shared PC/NPC provider for inventory-row status origins: the actor's own compiled
 * tree first, then each isolated foreign source snapshot, compiled once. */
export function createFromBaseStatusOriginInventory(base: StatusOriginInventoryBase, own: () => readonly FromBaseRow[],
    metadata: FromBaseInventoryMetadata): (id: string) => FromBaseRow | null {
    const originTrees = new Map<string, ReturnType<typeof compileInventoryTreeFromBase>>();
    return id => {
        const visit = (rows: readonly FromBaseRow[]): FromBaseRow | null => {
            for (const row of rows) {
                if (String(readInventoryField(row, 'inventory_id')).toUpperCase() === id.toUpperCase()) return row;
                if (Array.isArray(row.inventory_in_container)) { const child = visit(row.inventory_in_container as FromBaseRow[]); if (child) return child; }
            }
            return null;
        };
        const found = visit(own());
        if (found) return found;
        for (const snapshot of base.status_origin_inventory_sources ?? []) {
            if (!snapshot.inventory.some(row => String(readInventoryField(row, 'inventory_id')).toUpperCase() === id.toUpperCase())) continue;
            let sourceTree = originTrees.get(snapshot.entity_id);
            if (!sourceTree) {
                sourceTree = compileInventoryTreeFromBase({ ...snapshot,
                    enchantment_templates: snapshot.enchantment_templates ?? base.enchantment_templates!,
                    enchantment_template_enchantments: snapshot.enchantment_template_enchantments ?? base.enchantment_template_enchantments!,
                    missing_definition_ids: snapshot.missing_definition_ids ?? base.missing_definition_ids,
                    definition_videos: snapshot.definition_videos ?? base.definition_videos,
                    pl_signature: base.pl_signature, inventory_metadata: snapshot.inventory_metadata ?? base.inventory_metadata,
                    metadata: mergeInventoryMetadata(metadata, snapshot.inventory_metadata ?? base.inventory_metadata) }, { ...base.catalog, ...snapshot.catalog });
                originTrees.set(snapshot.entity_id, sourceTree);
            }
            if (!sourceTree.inventory || sourceTree.missingStages.length) throw new FromBaseStageMissing('status-environment-layering',
                `inventory-origin:${sourceTree.detail ?? sourceTree.missingStages.join(',')}`);
            const source = visit(sourceTree.inventory);
            if (source) return source;
        }
        throw new FromBaseInputMissing('status-origin-inventory-source', id);
    };
}
